class AmprioHeader {
  constructor() {
    this.isCartFetching = false;
    this.pendingCartUpdate = false;
    this.cachedElements = new Map();
    this.cartUpdateScheduled = false;
    this.lastItemCount = null;
    this.cartCache = null;
    this.cartCacheTime = 0;
    this.cartCacheTTL = 5000;

    this.init();
  }

  getElement(selector) {
    if (!this.cachedElements.has(selector)) {
      this.cachedElements.set(selector, document.querySelector(selector));
    }
    return this.cachedElements.get(selector);
  }

  getElements(selector) {
    return Array.from(document.querySelectorAll(selector));
  }

  init() {
    this.initCartUpdateEvents();
    this.initStickyHeader();
    this.initMenuHoverEffects();

    if (document.readyState === 'complete') {
      this.updateCartCount();
    } else {
      window.addEventListener('load', () => {
        setTimeout(() => this.updateCartCount(), 100);
      });
    }
  }


  initCartUpdateEvents() {
    let debounceTimer;
    
    const scheduleUpdate = (e) => {
      const itemCount = e?.detail?.cart?.item_count 
        ?? e?.detail?.data?.itemCount 
        ?? e?.detail?.item_count;
      
      if (e && e.detail && itemCount !== undefined) {
        this.applyCartCount(itemCount);
        
        if (e.detail.cart) {
          if (window.cartCache) {
            window.cartCache.invalidate();
          }
          if (window.setCart) {
            window.setCart(e.detail.cart);
          }
        }
        return;
      }
      
      if (window.cartCache) {
        window.cartCache.invalidate();
      }
      
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        this.updateCartCount();
      }, 100);
    };

    [
      'shopify:cart:update',
      'cart:update',
      'cart:add',
      'cart:change',
      'cart:updated'
    ].forEach(event =>
      document.addEventListener(event, scheduleUpdate, { passive: true })
    );
  }

  updateCartCount(count = null) {
    const textElements = this.getElements('.cart-count__text');
    const containerElements = this.getElements('.cart-count');
    if (!textElements.length || !containerElements.length) return;

    if (count !== null) {
      this.applyCartCount(count);
      return;
    }

    if (window.cartCache) {
      window.cartCache.get()
        .then(cart => {
          if (cart) {
            this.applyCartCount(cart.item_count);
          }
        })
        .catch(() => {});
    } else {
      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          this.applyCartCount(cart.item_count);
        })
        .catch(() => {});
    }
  }

  applyCartCount(count) {
    const newCount = parseInt(count) || 0;
    
    this.lastItemCount = newCount;

    const textElements = Array.from(document.querySelectorAll('.cart-count__text'));
    const containerElements = Array.from(document.querySelectorAll('.cart-count'));

    textElements.forEach(textEl => {
      if (textEl) {
        textEl.textContent = newCount;
      }
    });

    containerElements.forEach(containerEl => {
      if (!containerEl) return;

      if (newCount > 0) {
        containerEl.style.display = 'flex';
        containerEl.classList.add('cart-has-items');
      } else {
        containerEl.classList.remove('cart-has-items');
      }

      containerEl.style.opacity = '1';
    });
  }

  initStickyHeader() {
    let attempts = 0;
    const maxAttempts = 10;
    let mobileControlsOriginalTop = null;
    let filtersBarOriginalTop = null;

    const tryInit = () => {
      attempts++;

      const header = document.querySelector('.amprio-header');
      const menu = document.querySelector('.block_navigation_menu');
      const mobileMenu = document.querySelector('.block_navigation_header_mob');
      const isMobile = window.innerWidth <= 1024;
      
      if (!header) {
        if (attempts < maxAttempts) {
          setTimeout(tryInit, 100);
        }
        return;
      }
      
      if (!isMobile && !menu) {
        if (attempts < maxAttempts) {
          setTimeout(tryInit, 100);
        }
        return;
      }
      
      if (isMobile && !mobileMenu) {
        if (attempts < maxAttempts) {
          setTimeout(tryInit, 100);
        }
        return;
      }

      const mode = header.dataset.sticky;
      if (mode === 'never' && !isMobile) return;

      let lastY = window.scrollY;
      let isSticky = false;
      let ticking = false;

      const mobileControls = document.querySelector('.collection-mobile-controls');
      if (mobileControls && mobileControlsOriginalTop === null) {
        mobileControlsOriginalTop = mobileControls.offsetTop;
      }

      const update = () => {
        const y = window.scrollY;
        const threshold = header.offsetHeight;
        const currentIsMobile = window.innerWidth <= 1024;

        if (!currentIsMobile && menu) {
          if (mode === 'always') {
            const shouldBeSticky = y > threshold;
            if (shouldBeSticky !== isSticky) {
              menu.classList.toggle('block_navigation_menu--sticky', shouldBeSticky);
              isSticky = shouldBeSticky;
            }

          } else if (mode === 'scroll-up') {
            if (y > threshold) {
              const scrollingDown = y > lastY && y > 120;
              const shouldBeSticky = !scrollingDown;
              if (shouldBeSticky !== isSticky) {
                menu.classList.toggle('block_navigation_menu--sticky', shouldBeSticky);
                isSticky = shouldBeSticky;
              }
            } else if (isSticky) {
              menu.classList.remove('block_navigation_menu--sticky');
              isSticky = false;
            }
          }
        }

        if (mobileMenu && currentIsMobile) {
          const filtersBar = document.querySelector('.collection-filters-bar__mobile');
          const filtersBarPlaceholder = document.querySelector('.collection-filters-bar__mobile-placeholder');
          const topBanner = document.querySelector('.top-banner');
          const topBannerHeight = topBanner ? topBanner.offsetHeight : 32;
          
          if (filtersBar) {
            if (filtersBarOriginalTop === null) {
              filtersBarOriginalTop = filtersBar.getBoundingClientRect().top + window.scrollY;
            }
            
            const stickyTopPosition = topBannerHeight;
            const triggerPoint = filtersBarOriginalTop - stickyTopPosition;
            
            if (y >= triggerPoint) {
              mobileMenu.classList.remove('block_navigation_header_mob--sticky');
              filtersBar.classList.add('collection-filters-bar__mobile--sticky');
              
              if (filtersBarPlaceholder) {
                filtersBarPlaceholder.style.height = filtersBar.offsetHeight + 'px';
                filtersBarPlaceholder.classList.add('collection-filters-bar__mobile-placeholder--active');
              }
            } else {
              if (y > topBannerHeight) {
                mobileMenu.classList.add('block_navigation_header_mob--sticky');
              } else {
                mobileMenu.classList.remove('block_navigation_header_mob--sticky');
              }
              
              filtersBar.classList.remove('collection-filters-bar__mobile--sticky');
              
              if (filtersBarPlaceholder) {
                filtersBarPlaceholder.classList.remove('collection-filters-bar__mobile-placeholder--active');
                filtersBarPlaceholder.style.height = '0';
              }
            }
          } else {
            if (y > topBannerHeight) {
              mobileMenu.classList.add('block_navigation_header_mob--sticky');
            } else {
              mobileMenu.classList.remove('block_navigation_header_mob--sticky');
            }
          }
          
          const mobileHeaderHeight = mobileMenu.offsetHeight || 48;
          
          if (mobileControls && mobileControlsOriginalTop !== null && y >= 290) {
            const controlsOriginalTop = mobileControlsOriginalTop;
            const stickyMenuTop = y;
            
            if (stickyMenuTop >= controlsOriginalTop) {
              mobileControls.classList.add('collection-mobile-controls--sticky');
              mobileMenu.classList.remove('block_navigation_header_mob--sticky');
            } else {
              mobileControls.classList.remove('collection-mobile-controls--sticky');
            }
          } else if (mobileControls && y < 290) {
            mobileControls.classList.remove('collection-mobile-controls--sticky');
          }
        }

        lastY = y;
        ticking = false;
      };

      const handleScroll = () => {
        if (!ticking) {
          requestAnimationFrame(update);
          ticking = true;
        }
      };

      window.addEventListener('scroll', handleScroll, { passive: true });

      window.addEventListener('resize', () => {
        if (window.innerWidth > 1024 && typeof window.closeMobileMenuDrawer === 'function') {
          window.closeMobileMenuDrawer();
        }
      });
      
      update();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(tryInit, 100);
      });
    } else {
      setTimeout(tryInit, 100);
    }
  }

  initMenuHoverEffects() {
    const setupMenuHover = () => {
      const submenus = this.getElements('.btn_menu__submenu');

      submenus.forEach(submenu => {
        const collectionImg = submenu.querySelector('.collection_img__image');
        const collectionLink = submenu.querySelector('.collection_img__link');
        
        if (!collectionImg) return;

        const defaultImage = collectionImg.dataset.defaultImage || collectionImg.src;
        const defaultUrl = collectionLink?.dataset.defaultUrl || collectionLink?.href || '';
        const defaultTitle = collectionImg.dataset.defaultTitle || collectionImg.alt || '';

        if (!defaultImage) return;

        const imageCache = new Map();
        const menuSubItems = submenu.querySelectorAll('[data-menu-sub-item]');
        let currentHoveredItem = null;

        menuSubItems.forEach(item => {
          const imageUrl = item.dataset.collectionImage;
          const collectionUrl = item.dataset.collectionUrl;
          const title = item.dataset.collectionTitle;

          if (!imageUrl) return;

          if (!imageCache.has(imageUrl)) {
            const img = new Image();
            img.src = imageUrl;
            imageCache.set(imageUrl, img);
          }

          const changeImage = (newSrc, newAlt, newUrl) => {
            if (collectionImg.src === newSrc) return;
            
            const cachedImage = imageCache.get(newSrc);
            if (cachedImage && cachedImage.complete) {
              collectionImg.src = newSrc;
              collectionImg.alt = newAlt;
              if (collectionLink && newUrl) {
                collectionLink.href = newUrl;
              }
            } else if (cachedImage) {
              cachedImage.onload = () => {
                if (currentHoveredItem === item || newSrc === defaultImage) {
                  collectionImg.src = newSrc;
                  collectionImg.alt = newAlt;
                  if (collectionLink && newUrl) {
                    collectionLink.href = newUrl;
                  }
                }
              };
            }
          };

          const handleMouseEnter = (e) => {
            e.stopPropagation();
            currentHoveredItem = item;
            changeImage(imageUrl, title || defaultTitle, collectionUrl);
          };

          const handleMouseLeave = (e) => {
            e.stopPropagation();
            
            if (currentHoveredItem === item) {
              currentHoveredItem = null;
              
              const stillHovered = Array.from(menuSubItems).some(i => 
                i.matches(':hover') || i.querySelector(':hover')
              );
              
              if (!stillHovered && defaultImage && collectionImg) {
                changeImage(defaultImage, defaultTitle, defaultUrl);
              }
            }
          };

          item.addEventListener('mouseenter', handleMouseEnter);
          item.addEventListener('mouseleave', handleMouseLeave);

          const submenuLink = item.querySelector('.btn_menu_sub__link');
          if (submenuLink) {
            submenuLink.addEventListener('mouseenter', handleMouseEnter);
            submenuLink.addEventListener('mouseleave', handleMouseLeave);
          }
        });

        const handleSubmenuLeave = () => {
          currentHoveredItem = null;
          
          if (defaultImage && collectionImg && collectionImg.src !== defaultImage) {
            collectionImg.src = defaultImage;
            collectionImg.alt = defaultTitle;

            if (collectionLink && defaultUrl) {
              collectionLink.href = defaultUrl;
            }
          }
        };

        submenu.addEventListener('mouseleave', handleSubmenuLeave);
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setupMenuHover);
    } else {
      setupMenuHover();
    }

    document.addEventListener('shopify:section:load', setupMenuHover);
  }
}

window.openCart = function () {
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer?.open) drawer.open();
  else location.href = '/cart';
};

window.updateCartCount = function (count = null) {
  window.amprioHeaderInstance?.updateCartCount(count);
};

function initHeaderOnce() {
  if (!window.amprioHeaderInstance) {
    window.amprioHeaderInstance = new AmprioHeader();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeaderOnce);
} else {
  initHeaderOnce();
}

document.addEventListener('shopify:section:load', initHeaderOnce);

(function() {
  function getMarketFromPath(pathname) {
    const arPrefix = pathname.match(/^\/ar-([a-z]{2})(?:$|\/)/);
    if (arPrefix) return { locale: 'ar', country: arPrefix[1].toLowerCase() };
    const enPrefix = pathname.match(/^\/en-([a-z]{2})(?:$|\/)/);
    if (enPrefix) return { locale: 'en', country: enPrefix[1].toLowerCase() };
    if (pathname === '/ar' || pathname.startsWith('/ar/')) return { locale: 'ar', country: 'ae' };
    return { locale: 'en', country: 'ae' };
  }

  var countryNames = { AE: 'UAE', SA: 'KSA', KW: 'KWT', QA: 'QAT', OM: 'OMN', BH: 'BHR', US: 'USA', AU: 'AUS', DE: 'EU' };
  function syncCountryDisplay() {
    var market = getMarketFromPath(window.location.pathname);
    var name = countryNames[market.country.toUpperCase()] || market.country.toUpperCase();
    document.querySelectorAll('[data-country-display]').forEach(function(el) { el.textContent = name; });
  }

  function buildMarketUrl(locale, country, preservePath) {
    const c = country.toLowerCase();
    let basePath = '';
    if (c === 'ae') {
      basePath = locale === 'ar' ? '/ar' : '/';
    } else {
      basePath = '/' + locale + '-' + c;
    }
    
    if (preservePath) {
      const currentPath = window.location.pathname;
      const currentMarket = getMarketFromPath(currentPath);
      let pathWithoutPrefix = currentPath;
      
      if (currentMarket.locale && currentMarket.country && currentMarket.country !== 'ae') {
        const oldPrefix = '/' + currentMarket.locale + '-' + currentMarket.country.toLowerCase();
        if (currentPath.startsWith(oldPrefix)) {
          pathWithoutPrefix = currentPath.substring(oldPrefix.length) || '/';
        }
      } else if (currentPath === '/ar' || currentPath.startsWith('/ar/')) {
        pathWithoutPrefix = currentPath === '/ar' ? '/' : currentPath.substring(3);
      }
      
      if (pathWithoutPrefix === '/' && basePath !== '/') {
        return basePath;
      }
      if (pathWithoutPrefix !== '/' && basePath === '/') {
        return pathWithoutPrefix;
      }
      if (pathWithoutPrefix !== '/' && basePath !== '/') {
        return basePath + pathWithoutPrefix;
      }
    }
    
    return basePath;
  }

  const USER_WRAPPER_SELECTOR = '.user-dropdown-wrapper';
  const USER_BUTTON_SELECTOR = '#mobile-header-user-btn, .mobile-header__user-btn, #header-user-btn, .header-user-btn';
  const USER_DROPDOWN_SELECTOR = '.user-dropdown';
  let userDropdownEventsBound = false;

  function closeUserDropdowns(exceptWrapper) {
    document.querySelectorAll(USER_WRAPPER_SELECTOR).forEach(function(wrapper) {
      if (exceptWrapper && wrapper === exceptWrapper) return;
      wrapper.removeAttribute('data-dropdown-open');
      const button = wrapper.querySelector(USER_BUTTON_SELECTOR);
      if (button) button.setAttribute('aria-expanded', 'false');
    });
  }

  function toggleUserDropdown(wrapper) {
    if (!wrapper) return;
    const isOpen = wrapper.getAttribute('data-dropdown-open') === 'true';
    if (isOpen) {
      closeUserDropdowns();
      return;
    }
    closeUserDropdowns(wrapper);
    wrapper.setAttribute('data-dropdown-open', 'true');
    const button = wrapper.querySelector(USER_BUTTON_SELECTOR);
    if (button) button.setAttribute('aria-expanded', 'true');
  }

  function initUserDropdowns() {
    document.querySelectorAll(USER_WRAPPER_SELECTOR).forEach(function(wrapper) {
      const button = wrapper.querySelector(USER_BUTTON_SELECTOR);
      const dropdown = wrapper.querySelector(USER_DROPDOWN_SELECTOR);
      if (!button || !dropdown) return;
      button.setAttribute('aria-haspopup', 'menu');
      button.setAttribute('aria-expanded', wrapper.getAttribute('data-dropdown-open') === 'true' ? 'true' : 'false');
    });

    if (userDropdownEventsBound) return;

    document.addEventListener('click', function(e) {
      const button = e.target.closest(USER_BUTTON_SELECTOR);
      if (button) {
        e.preventDefault();
        e.stopPropagation();
        toggleUserDropdown(button.closest(USER_WRAPPER_SELECTOR));
        return;
      }
      if (!e.target.closest(USER_WRAPPER_SELECTOR)) closeUserDropdowns();
    });

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') closeUserDropdowns();
    });

    userDropdownEventsBound = true;
  }

  initUserDropdowns();
  document.addEventListener('shopify:section:load', initUserDropdowns);

  document.addEventListener('click', function(e) {
    const burger = e.target.closest('.btn_menu_burger');
    
    if (burger) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      const drawer = document.getElementById('mobile-menu-drawer');
      if (drawer) {
        if (drawer.parentNode !== document.body) {
          document.body.appendChild(drawer);
        }
        if (typeof window.openMobileMenuDrawer === 'function') {
          window.openMobileMenuDrawer();
        } else {
          var scrollY = window.scrollY;
          document.body.style.top = '-' + scrollY + 'px';
          document.body.style.left = '0';
          document.body.style.right = '0';
          document.body.classList.add('menu-drawer-open');
          drawer.classList.remove('is-hidden');
          requestAnimationFrame(function() {
            drawer.classList.add('is-open');
          });
        }
      }
      return;
    }
    
  }, true);

  const MARKET_STORAGE_KEY = 'amprio_selected_market';
  
  function saveMarketToCache(countryCode, locale, deliveryData) {
    try {
      const existing = getMarketFromCache() || {};
      const code = (countryCode || 'AE').toString().toUpperCase();
      const prev = (existing.countryCode || existing.country || '').toString().toUpperCase();
      const data = {
        ...existing,
        country: code.toLowerCase(),
        countryCode: code,
        locale: locale
      };
      if (prev && prev !== code && !deliveryData) {
        data.city = '';
        data.area = '';
        data.countryName = '';
        data.countryOnly = false;
      }
      if (deliveryData) {
        data.city = deliveryData.city || '';
        data.area = deliveryData.area || '';
        data.countryName = deliveryData.countryName || '';
        data.countryOnly = deliveryData.countryOnly || false;
      }
      localStorage.setItem(MARKET_STORAGE_KEY, JSON.stringify(data));
    } catch (_) {}
  }
  
  function getMarketFromCache() {
    try {
      const cached = localStorage.getItem(MARKET_STORAGE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch (_) { return null; }
  }

  function redirectToMarket(countryCode) {
    if (!countryCode) return;
    const code = countryCode.toUpperCase();
    const available = ['AE', 'SA', 'QA', 'KW', 'OM', 'BH', 'DE', 'US', 'AU'];
    if (available.indexOf(code) === -1) return;
    const pathname = window.location.pathname;
    const market = getMarketFromPath(pathname);
    if (code.toLowerCase() === market.country) return;
    saveMarketToCache(code, market.locale);
    const newUrl = buildMarketUrl(market.locale, code, true);
    const search = window.location.search;
    const hash = window.location.hash;
    window.location.href = newUrl + search + hash;
  }
  
  function checkAndRestoreMarket() {
    try {
      const pathname = window.location.pathname;
      const currentMarket = getMarketFromPath(pathname);
      const pathCountry = (currentMarket.country || 'ae').toLowerCase();
      const locale = currentMarket.locale || 'en';
      saveMarketToCache(pathCountry.toUpperCase(), locale);
    } catch (_) {}
  }

  let countrySelectorInitialized = false;
  
  function initCountrySelector() {
    if (countrySelectorInitialized) return;
    
    const countryBtns = document.querySelectorAll('[data-popup-trigger="delivery-location"]');
    const langBtns = document.querySelectorAll('[data-lang-switch]');
    const popup = document.getElementById('am-popup-delivery-location');
    
    if (!countryBtns.length && !langBtns.length) return;
    
    countrySelectorInitialized = true;
    
    if (countryBtns.length && popup) {
      countryBtns.forEach(function(countryBtn) {
        countryBtn.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          popup.style.display = 'flex';
          requestAnimationFrame(function() {
            popup.classList.add('am-popup--active');
            document.body.classList.add('am-popup-open');
          });
        });
      });
      
      const confirmBtn = document.getElementById('delivery-location-confirm-region');
      if (confirmBtn) {
        confirmBtn.addEventListener('click', function(e) {
          const countrySelect = document.getElementById('delivery-location-country');
          if (countrySelect && countrySelect.value) {
            e.preventDefault();
            e.stopPropagation();
            const selected = countrySelect.value;
            const pathname = window.location.pathname;
            const market = getMarketFromPath(pathname);
            if (selected.toLowerCase() !== market.country) {
              redirectToMarket(selected);
            } else {
              popup.dispatchEvent(new CustomEvent('popup:close', { bubbles: true }));
              popup.classList.remove('am-popup--active');
              document.body.classList.remove('am-popup-open');
              setTimeout(function() { popup.style.display = 'none'; }, 300);
            }
          }
        });
      }
    }
    
    if (popup) {
      popup.addEventListener('popup:close', function() {
        const countrySelect = document.querySelector('[data-delivery-country]');
        if (countrySelect && countrySelect.value) {
          const selected = countrySelect.value;
          const market = getMarketFromPath(window.location.pathname);
          if (selected.toLowerCase() !== market.country) {
            redirectToMarket(selected);
          }
        }
      }, { once: false });
    }
    
    langBtns.forEach(function(langBtn) {
      langBtn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        const market = getMarketFromPath(window.location.pathname);
        const nextLocale = market.locale === 'ar' ? 'en' : 'ar';
        saveMarketToCache(market.country, nextLocale);
        const nextUrl = buildMarketUrl(nextLocale, market.country, true);
        const search = window.location.search;
        const hash = window.location.hash;
        window.location.href = nextUrl + search + hash;
      });
    });
    

  }
  
  function showComingSoonToast(feature) {
    let toast = document.getElementById('amprio-coming-soon-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'amprio-coming-soon-toast';
      toast.style.cssText = 'position:fixed;bottom:20px;left:50%;transform:translateX(-50%);background:#3F4443;color:#fff;padding:12px 24px;border-radius:8px;font-family:"Cabinet Grotesk",sans-serif;font-size:14px;z-index:99999;opacity:0;transition:opacity 0.3s ease;pointer-events:none;';
      document.body.appendChild(toast);
    }
    toast.textContent = feature + ' — Coming soon';
    toast.style.opacity = '1';
    clearTimeout(toast._timeout);
    toast._timeout = setTimeout(function() {
      toast.style.opacity = '0';
    }, 2000);
  }

  const RATES_CACHE_KEY = 'amprio_exchange_rates';
  const BASE_CURRENCY = 'AED';
  const STABLE_PER_AED_USD_AUD = { USD: 0.2723, AUD: 0.3856 };

  function applyStableUsdAudRates(rates) {
    if (!rates) return;
    rates.USD = STABLE_PER_AED_USD_AUD.USD;
    rates.AUD = STABLE_PER_AED_USD_AUD.AUD;
  }

  const currencyMap = {
    'AE': 'AED',
    'SA': 'SAR',
    'KW': 'KWD',
    'QA': 'QAR',
    'OM': 'OMR',
    'BH': 'BHD',
    'US': 'USD',
    'AU': 'AUD',
    'DE': 'EUR', 'FR': 'EUR', 'IT': 'EUR', 'ES': 'EUR', 'NL': 'EUR', 'BE': 'EUR',
    'AT': 'EUR', 'PT': 'EUR', 'IE': 'EUR', 'PL': 'EUR', 'RO': 'EUR', 'CZ': 'EUR',
    'GR': 'EUR', 'SE': 'EUR', 'HU': 'EUR', 'FI': 'EUR', 'DK': 'EUR', 'SK': 'EUR',
    'SI': 'EUR', 'LU': 'EUR', 'EE': 'EUR', 'LV': 'EUR', 'LT': 'EUR', 'MT': 'EUR',
    'CY': 'EUR', 'BG': 'EUR', 'HR': 'EUR'
  };

  let exchangeRates = {
    AED: 1,
    SAR: 1.0208,
    KWD: 0.0834,
    QAR: 0.9907,
    OMR: 0.1047,
    BHD: 0.1025,
    USD: STABLE_PER_AED_USD_AUD.USD,
    AUD: STABLE_PER_AED_USD_AUD.AUD,
    EUR: 0.2513
  };

  let ratesLoaded = false;

  function loadRatesFromCache() {
    try {
      const cached = localStorage.getItem(RATES_CACHE_KEY);
      if (cached) {
        const data = JSON.parse(cached);
        if (data.date && data.rates) {
          const cachedDate = new Date(data.date);
          const now = new Date();
          if (cachedDate.toDateString() === now.toDateString()) {
            exchangeRates = data.rates;
            applyStableUsdAudRates(exchangeRates);
            ratesLoaded = true;
            return true;
          }
        }
      }
    } catch (e) {}
    return false;
  }

  function saveRatesToCache(rates) {
    try {
      localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({
        date: new Date().toISOString(),
        rates: rates
      }));
    } catch (e) {}
  }

  function fetchExchangeRatesOnce() {
    if (ratesLoaded) return;

    var displayCurrency = (window.theme && window.theme.currency) || BASE_CURRENCY;
    if (displayCurrency === BASE_CURRENCY) {
      ratesLoaded = true;
      return;
    }

    if (loadRatesFromCache()) return;

    function runFetch() {
      if (ratesLoaded) return;
      fetch('https://v6.exchangerate-api.com/v6/354a0738862350f0a52a816e/latest/' + BASE_CURRENCY)
      .then(function(response) {
        if (!response.ok) throw new Error('Failed to fetch rates');
        return response.json();
      })
      .then(function(data) {
        if (data && data.conversion_rates) {
          exchangeRates = data.conversion_rates;
          exchangeRates[BASE_CURRENCY] = 1;
          applyStableUsdAudRates(exchangeRates);
          saveRatesToCache(exchangeRates);
          ratesLoaded = true;
          document.dispatchEvent(new CustomEvent('amprio:convert-prices'));
        }
      })
      .catch(function(err) {
        console.warn('Exchange rates fetch failed, using fallback rates');
        applyStableUsdAudRates(exchangeRates);
        ratesLoaded = true;
      });
    }

    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(runFetch, { timeout: 3500 });
    } else {
      setTimeout(runFetch, 1);
    }
  }

  loadRatesFromCache();

  function convertPrice(priceInMinor, fromCurrency, toCurrency) {
    if (fromCurrency === toCurrency) return priceInMinor;
    
    const fromRate = exchangeRates[fromCurrency] || 1;
    const toRate = exchangeRates[toCurrency] || 1;
    
    const priceInBase = priceInMinor / fromRate;
    const convertedPrice = priceInBase * toRate;
    
    return Math.round(convertedPrice);
  }

  function formatPrice(priceInMinor) {
    const price = priceInMinor / 100;
    return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function currencyCodeToSuffix(code) {
    return code === 'USD' ? '' : code;
  }

  function pathnameHasExplicitMarketPrefix(pathname) {
    if (!pathname) return false;
    if (pathname === '/ar' || pathname.indexOf('/ar/') === 0) return true;
    return /^\/[a-z]{2}-[a-z]{2}(\/|$)/i.test(pathname);
  }

  function updateCurrencyFromPath() {
    const pathname = window.location.pathname || '';
    if (pathnameHasExplicitMarketPrefix(pathname)) {
      const market = getMarketFromPath(pathname);
      const countryCode = market.country.toUpperCase();
      const newCurrency = currencyMap[countryCode] || 'AED';
      if (window.theme) {
        window.theme.country = countryCode;
        window.theme.currency = newCurrency;
      }
    }
    document.dispatchEvent(new CustomEvent('amprio:convert-prices'));
  }

  function handleCurrencyConversion() {
    const targetCurrency = window.theme?.currency || 'AED';
    
    const currencySelectors = [
      '[data-price-currency]',
      '[data-compare-currency]',
      '[data-tabby-currency]',
      '.item-selection-product-price [data-price-currency]',
      '.item-selection-product-price [data-compare-currency]'
    ];
    
    const currencySuffix = currencyCodeToSuffix(targetCurrency);
    currencySelectors.forEach(function(selector) {
      document.querySelectorAll(selector).forEach(function(el) {
        if (el.textContent !== currencySuffix) {
          el.textContent = currencySuffix;
        }
      });
    });

    document.querySelectorAll('[data-price-minor]').forEach(function(container) {
      const minorRaw = container.getAttribute('data-price-minor');
      const originalPrice = parseInt(minorRaw == null ? '' : minorRaw, 10);
      const originalCurrency = (container.getAttribute('data-currency') || BASE_CURRENCY).trim() || BASE_CURRENCY;

      if (isNaN(originalPrice)) return;

      const convertedPrice = convertPrice(originalPrice, originalCurrency, targetCurrency);
      const formattedPrice = formatPrice(convertedPrice);

      const priceValueEl = container.querySelector('[data-price-value]');
      if (priceValueEl) {
        priceValueEl.textContent = targetCurrency === 'USD' ? '$' + formattedPrice : formattedPrice;
      }

      const compareAttr = container.getAttribute('data-compare-minor');
      if (compareAttr != null && compareAttr !== '') {
        const originalCompare = parseInt(compareAttr, 10);
        if (!isNaN(originalCompare)) {
          const convertedCompare = convertPrice(originalCompare, originalCurrency, targetCurrency);
          const formattedCompare = formatPrice(convertedCompare);

          const compareValueEl = container.querySelector('[data-compare-value]');
          if (compareValueEl) {
            compareValueEl.textContent = targetCurrency === 'USD' ? '$' + formattedCompare : formattedCompare;
          }
        }
      }
    });

    if (targetCurrency === 'USD') {
      document.querySelectorAll('[data-tabby-price]').forEach(function(el) {
        var raw = (el.textContent || '').replace(/^\$\s*/, '').trim();
        if (raw) el.textContent = '$' + raw;
      });
    }
  }

  document.addEventListener('amprio:convert-prices', handleCurrencyConversion);

  document.addEventListener('variant:update', function() {
    requestAnimationFrame(function() {
      document.dispatchEvent(new CustomEvent('amprio:convert-prices'));
    });
  });

  document.addEventListener('shopify:section:load', function() {
    requestAnimationFrame(function() {
      document.dispatchEvent(new CustomEvent('amprio:convert-prices'));
    });
  });
  
  window.amprioConvertPrice = convertPrice;
  window.amprioFormatPrice = formatPrice;
  window.amprioCurrencySuffix = currencyCodeToSuffix;
  window.amprioExchangeRates = function() { return exchangeRates; };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      fetchExchangeRatesOnce();
      checkAndRestoreMarket();
      initCountrySelector();
      syncCountryDisplay();
      updateCurrencyFromPath();
    });
  } else {
    fetchExchangeRatesOnce();
    checkAndRestoreMarket();
    initCountrySelector();
    syncCountryDisplay();
    updateCurrencyFromPath();
  }
})();

if (typeof module !== 'undefined') module.exports = AmprioHeader;